import { useEffect, useMemo } from "react";
import {
  Button,
  ButtonVariants,
} from "../../../../components/core/Button/Button";
import { FormField } from "../../../../components/core/FormField/FormField";
import { Loader } from "../../../../components/core/Loader/Loader";
import TextInput from "../../../../components/core/TextInput/TextInput";
import { FormSection } from "../../../../components/FormSection/FormSection";
import {
  ADD_NEW_COURSE,
  SAVE_LABEL,
  CANCEL_LABEL,
  DETAILS_LABEL,
  DURATION_LABEL,
  TYPOLOGY_LABEL,
  EXECUTION_MODE_LABEL,
  CATEGORY_LABEL,
  URL_LABEL,
  DESCRIPTION_LABEL,
  TARGETS_LABEL,
  NAME_LABEL,
  IS_REQUIRED_LABEL,
  INTERNAL_GRADE_IT_LABEL,
  EDIT_COURSE_LABEL,
  COLLABORATOR_LABEL,
  CHANGE_DESTINATION_ALERT_MESSAGE,
  IS_NOT_VALID_URL_LABEL,
} from "../../../../constants/Dictionary";
import { StyledAddCourseModal, StyledAlertMessage } from "./CourseModal.styled";
import { Modal } from "../../../../components/core/Modal/Modal";
import {
  Select,
  SelectOption,
  SelectVariantEnum,
} from "../../../../components/core/Select/Select";
import { Divider } from "../../../../components/core/Divider/Divider";
import { Checkbox } from "../../../../components/core/Checkbox/Checkbox";
import { ReactComponent as CancelIcon } from "../../../../icons/close.svg";
import { ReactComponent as SaveIcon } from "../../../../icons/save.svg";
import { ReactComponent as BookOpenIcon } from "../../../../icons/bookOpen.svg";
import { ReactComponent as UserCircleIcon } from "../../../../icons/userCircle.svg";
import { CourseRequest, Course } from "../../../../types/api/courses.type";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, Resolver, useForm } from "react-hook-form";
import * as yup from "yup";
import { FontWeightEnum, IconSizeEnum } from "../../../../types/AppTypes";
import { IconManager } from "../../../../components/core/IconManager/IconManager";
import TextArea from "../../../../components/core/TextArea/TextArea";
import { useAppSelector } from "../../../../hooks/store.hook";
import { SelectTimespan } from "../../../../components/SelectTimespan/SelectTimespan";
import { BodyTyp } from "../../../../components/Typography/Typography";
import { theme } from "../../../../constants/StyledComponents";
import {
  CHARACTER_MAX_LENGTH,
  TimestampLabelEnum,
} from "../../../../constants/AppConstants";
import { ReactComponent as WarningIcon } from "../../../../icons/warning.svg";
import { OrgAccordion } from "../../../../components/OrgAccordion/OrgAccordion";
import { useSaveFormationCourse } from "../../../../hooks/formationCourse.hook";
import { useFormationCollaborators } from "../../../../hooks/formationCollaborators.hook";

type FormData = {
  name: string;
  description: string;
  category: string | null;
  typology: string | null;
  executionMode: string | null;
  duration?: string | null;
  url?: string;
  cc?: string[];
  internalGrade?: string[] | null;
  collaboratorId?: number | null;
  adHocCollaborator: boolean;
};

type CourseModalProps = {
  isOpen: boolean;
  closeModal: () => void;
  record?: Course;
};

export const CourseModal = ({
  isOpen,
  closeModal,
  record,
}: CourseModalProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);
  const { mutateAsync: saveCourse, isPending: isSavingCourse } =
    useSaveFormationCourse();

  const schema = yup
    .object({
      name: yup.string().required(`${NAME_LABEL} ${IS_REQUIRED_LABEL}`),
      duration: yup.string().nullable(),
      typology: yup
        .string()
        .nullable()
        .required(`${TYPOLOGY_LABEL} ${IS_REQUIRED_LABEL}`),
      category: yup
        .string()
        .nullable()
        .required(`${CATEGORY_LABEL} ${IS_REQUIRED_LABEL}`),
      executionMode: yup
        .string()
        .nullable()
        .required(`${EXECUTION_MODE_LABEL} ${IS_REQUIRED_LABEL}`),
      url: yup.string().url(`${URL_LABEL} ${IS_NOT_VALID_URL_LABEL}`),
      description: yup
        .string()
        .required(`${DESCRIPTION_LABEL} ${IS_REQUIRED_LABEL}`),
      internalGrade: yup.array(yup.string().required()).nullable(),
      collaboratorId: yup
        .number()
        .nullable()
        .when(["adHocCollaborator"], {
          is: true,
          then: (field) =>
            field.required(`${COLLABORATOR_LABEL} ${IS_REQUIRED_LABEL}`),
        }),
      area: yup.array().of(yup.string().required()),
      practice: yup.array().of(yup.string().required()),
      cc: yup.array().of(yup.string().required()),
      adHocCollaborator: yup.boolean().required(),
    })
    .required();

  const {
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<FormData>({
    resolver: yupResolver(schema) as Resolver<FormData>,
  });

  const onSubmit = async (data: FormData) => {
    const body: CourseRequest = {
      name: data.name,
      duration: data.duration ?? null,
      typology: data.typology!,
      category: data.category!,
      executionMode: data.executionMode!,
      url: data.url ? data.url : null,
      description: data.description,
      cc: data.cc ?? [],
      internalGrade: data.internalGrade ?? null,
      adHocCollaborator: data.adHocCollaborator,
      collaboratorId: data.collaboratorId ?? null,
    };

    await saveCourse({ body, record });

    closeModal();
  };

  useEffect(() => {
    if (record) {
      reset({
        name: record.name,
        ...(record.duration && { duration: record.duration }),
        typology: record.typology,
        category: record.category,
        executionMode: record.executionMode,
        url: record.url ?? undefined,
        description: record.description,
        adHocCollaborator: record.adHocCollaborator,
        cc: record.cc,
        internalGrade: record.internalGrade ?? null,
        ...(record.collaborator && { collaboratorId: record.collaborator.id }),
      });
    } else {
      reset({
        name: "",
        duration: null,
        typology: null,
        category: null,
        executionMode: null,
        url: "",
        description: "",
        adHocCollaborator: false,
        cc: [],
        internalGrade: null,
        collaboratorId: null,
      });
    }
  }, [record]);

  const selectedAdPersonam = watch("adHocCollaborator");

  const resetTargets = () => {
    setValue("cc", []);
    setValue("internalGrade", null);
    setValue("collaboratorId", null);
  };

  const onClose = () => {
    closeModal();
  };

  const targetsAreChanged =
    record &&
    (dirtyFields.adHocCollaborator ||
      dirtyFields.collaboratorId ||
      dirtyFields.cc ||
      dirtyFields.internalGrade);

  const defaultCollaborator =
    record && record.collaborator
      ? {
          value: record.collaborator.id,
          label: `${record.collaborator.surname} ${record.collaborator.name}`,
        }
      : null;

  const {
    data: collaboratorData,
    initLoading: loadingCollaborators,
    canLoadMore: hasMore,
    search: searchValue,
    setSearch: setSearchValue,
    fetchMore,
    fetchingMore,
  } = useFormationCollaborators({
    queryKey: "course-modal-collaborators",
  });

  const collaborators = useMemo<SelectOption<number>[]>(() => {
    const options = collaboratorData.map((c) => ({
      value: c.id,
      label: `${c.surname} ${c.name}`,
    }));
    if (
      defaultCollaborator &&
      !options.find((o) => o.value === defaultCollaborator.value)
    ) {
      return [defaultCollaborator, ...options];
    }
    return options;
  }, [collaboratorData, defaultCollaborator]);

  return (
    <Modal
      label={record ? EDIT_COURSE_LABEL : ADD_NEW_COURSE}
      isOpen={isOpen}
      {...(targetsAreChanged && {
        message: (
          <StyledAlertMessage>
            <IconManager size={IconSizeEnum.SMALL} color={theme.colors.red_1}>
              <WarningIcon />
            </IconManager>
            <BodyTyp
              color={theme.colors.red_1}
              fontWeight={FontWeightEnum.SEMIBOLD}
            >
              {CHANGE_DESTINATION_ALERT_MESSAGE}
            </BodyTyp>
          </StyledAlertMessage>
        ),
      })}
      rightButton={
        <Button
          icon={<SaveIcon />}
          disabled={Object.keys(dirtyFields).length === 0}
          label={SAVE_LABEL}
          type="submit"
          form="courseForm"
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={onClose}
        />
      }
    >
      <StyledAddCourseModal>
        <Loader loading={isSavingCourse}>
          <form id="courseForm" onSubmit={handleSubmit(onSubmit)}>
            <FormSection label={DETAILS_LABEL} icon={<BookOpenIcon />}>
              <div className="form-row">
                <FormField error={errors.name?.message}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={NAME_LABEL}
                        maxLength={255}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.duration?.message}>
                  <Controller
                    name="duration"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <SelectTimespan
                        label={DURATION_LABEL}
                        value={value ?? undefined}
                        onChange={(v) => onChange(v ?? null)}
                        timestampLabels={[
                          TimestampLabelEnum.hour,
                          TimestampLabelEnum.day,
                          TimestampLabelEnum.month,
                        ]}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.typology?.message}>
                  <Controller
                    name="typology"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.SINGLE_SELECT}
                        label={TYPOLOGY_LABEL}
                        options={orgSettings.courses.typologies}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
              <div className="form-row">
                <FormField error={errors.executionMode?.message}>
                  <Controller
                    name="executionMode"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.SINGLE_SELECT}
                        label={EXECUTION_MODE_LABEL}
                        options={orgSettings.courses.executionModes}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.category?.message}>
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.SINGLE_SELECT}
                        label={CATEGORY_LABEL}
                        options={orgSettings.courses.categories}
                        {...field}
                      />
                    )}
                  />
                </FormField>
                <FormField error={errors.url?.message}>
                  <Controller
                    name="url"
                    control={control}
                    render={({ field }) => (
                      <TextInput label={URL_LABEL} maxLength={255} {...field} />
                    )}
                  />
                </FormField>
              </div>
              <div>
                <FormField error={errors.description?.message}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        label={DESCRIPTION_LABEL}
                        maxLength={CHARACTER_MAX_LENGTH}
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
            </FormSection>
            <Divider />
            <FormSection
              label={TARGETS_LABEL}
              icon={<UserCircleIcon />}
              rightContent={
                <Controller
                  name="adHocCollaborator"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox
                      label={"Ad Personam"}
                      value={value}
                      onChange={(checked) => {
                        resetTargets();
                        onChange(checked);
                      }}
                    />
                  )}
                />
              }
            >
              <div
                className={`form-row ${!selectedAdPersonam ? "visible" : "hidden"}`}
              >
                <FormField error={errors.internalGrade?.message}>
                  <Controller
                    name="internalGrade"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <Select
                        variant={SelectVariantEnum.MULTI_SELECT}
                        label={INTERNAL_GRADE_IT_LABEL}
                        options={orgSettings.collaborator.internalGrades}
                        value={value ?? undefined}
                        onChange={(value) => {
                          onChange(value && value.length > 0 ? value : null);
                        }}
                      />
                    )}
                  />
                </FormField>
              </div>
              <div className={`${!selectedAdPersonam ? "visible" : "hidden"}`}>
                <Controller
                  name="cc"
                  control={control}
                  render={({ field }) => <OrgAccordion {...field} />}
                />
              </div>
              <div
                className={`form-row space ${selectedAdPersonam ? "visible" : "hidden"}`}
              >
                <FormField error={errors.collaboratorId?.message}>
                  <Controller
                    name="collaboratorId"
                    control={control}
                    render={({ field }) => (
                      <Select
                        variant={SelectVariantEnum.DEFAULT}
                        label={COLLABORATOR_LABEL}
                        options={collaborators}
                        onSearch={setSearchValue}
                        searchValue={searchValue}
                        loading={loadingCollaborators}
                        canLoadMore={hasMore}
                        fetchMore={fetchMore}
                        fetchingMore={fetchingMore}
                        withSearch
                        {...field}
                      />
                    )}
                  />
                </FormField>
              </div>
            </FormSection>
          </form>
        </Loader>
      </StyledAddCourseModal>
    </Modal>
  );
};
